import { Stack, Typography, Divider } from '@mui/material';
import React from 'react';
import ComparisonItemEntry from './ComparisonItemEntry';
import combineItemsAndRecs from '../CombineItemsAndRecs';
import { useTheme } from '@mui/system';

function ComparisonView({ physicianItems, physicianRecommendations, peerItems, seeItemDetails, physicianNames = {}, selectedItemId = null }) {
    const theme = useTheme();
    // Helper to sort by item name
    function sortByItemName(a, b) {
        const nameA = (a.item?.display_name || a.item?.name || a.rec?.item?.display_name || a.rec?.item?.name || '').toLowerCase();
        const nameB = (b.item?.display_name || b.item?.name || b.rec?.item?.display_name || b.rec?.item?.name || '').toLowerCase();
        return nameA.localeCompare(nameB);
    }
    // Using this helper function to get a combined list of my items and recommendations (this accounts for addition recs, the true includes the swaps/compliance recs, false includes the removals)
    const myItemsWithRecs = combineItemsAndRecs(physicianItems ?? [], physicianRecommendations ?? [], true, false);

    // Get item ids to determine which items are shared
    const allMyItemIds = new Set(myItemsWithRecs.map(ir => ir.item?.id || ir.rec?.item?.id));
    const peerItemIds = new Set(peerItems ? peerItems.map(pei => pei.id) : []);
    const sharedItemIds = new Set([...allMyItemIds].filter(id => peerItemIds.has(id)));

    // separate into three sections, shared items, peer items, my items
    const sharedItemsWithRecs = myItemsWithRecs
        .filter(ir => sharedItemIds.has(ir.item?.id || ir.rec?.item?.id))
        .map(({ item, rec }) => {
            const peerItem = peerItems.find(pei => pei.id === (item?.id || rec?.item?.id));
            return { item, rec, peerItem };
        })
        .sort(sortByItemName);

    const theirItems = (peerItems ? peerItems.filter(pei => !sharedItemIds.has(pei.id)) : [])
        .sort(sortByItemName);

    const myItems = myItemsWithRecs
        .filter(ir => !sharedItemIds.has(ir.item?.id || ir.rec?.item?.id))
        .sort(sortByItemName);
    
    const isSelected = (itemId) => {
        return selectedItemId === itemId;
    }

    return (
        <Stack backgroundColor={theme.palette.background.categoryView} boxShadow={6} pb={2} mb={2} sx={{ borderBottomLeftRadius: 4, borderBottomRightRadius: 4 }}>
            {sharedItemsWithRecs.length > 0 && (
                <Stack spacing={1} p={1.5}>
                    <Divider><Typography variant='heavy' fontSize='12px'>Shared Items</Typography></Divider>
                    <Stack spacing={1}>
                        {sharedItemsWithRecs.map(({ item, rec, peerItem }) => (
                            <ComparisonItemEntry key={item.id || rec.item.id} myItem={item} myRec={rec} peerItem={peerItem} seeItemDetails={seeItemDetails} physicianNames={physicianNames} isSelected={isSelected(item.id || rec.item.id)} />
                        ))}
                    </Stack>
                </Stack>
            )}
            {theirItems.length > 0 && (
                <Stack spacing={1} p={1.5}>
                    <Divider><Typography variant='heavy' fontSize='12px'>{physicianNames.second || 'Peer Items'}</Typography></Divider>
                    <Stack spacing={1}>
                        {theirItems.map((item) => (
                            <ComparisonItemEntry key={item.id} peerItem={item} seeItemDetails={seeItemDetails} physicianNames={physicianNames} isSelected={isSelected(item.id)} />
                        ))}
                    </Stack>
                </Stack>
            )}
            {myItems.length > 0 && (
                <Stack spacing={1} p={1.5}>
                    <Divider><Typography variant='heavy' fontSize='12px'>{physicianNames.first || 'My Items'}</Typography></Divider>
                    <Stack spacing={1}>
                        {myItems.map(({ item, rec }) => (
                            <ComparisonItemEntry key={item.id || rec.item.id} myItem={item} myRec={rec} seeItemDetails={seeItemDetails} physicianNames={physicianNames} isSelected={isSelected(item.id || rec.item.id)} />
                        ))}
                    </Stack>
                </Stack>
            )}
        </Stack>
    );
}

export default ComparisonView;